import { UrlService } from '@/common/url/url.service';
import { CheckLoginUseCase } from '@/core/domain/auth/usecases/check-login.usecase';
import { GtmCheckOut } from '@/core/domain/gtm/models/gtm-check-out.models';
import { GtmError } from '@/core/domain/gtm/models/gtm-error.models';
import { GtmPageView } from '@/core/domain/gtm/models/gtm-page-view.models';
import { SetDataLayerParamsUseCase } from '@/core/domain/gtm/usecases/setDataLayer.usecase';
import { PurchasePersistenceModel } from '@/core/domain/purchase-persistence/models/PurchasePersistence.model';
import { GetPurchaseUseCase } from '@/core/domain/purchase-persistence/usecases/get-purchase-persistence.usecase';
import { CardToken } from '@/core/domain/recurly/models/CardToken';
import { PurchaseCustomModel } from '@/core/domain/recurly/models/PurchaseCustomModel';
import {
    CreateCardTokenRecurlyUseCaseInput,
    CreateTokenCardUseCase,
} from '@/core/domain/recurly/usecases/recurly-create-card-token.usecase';
import { CreatePurchaseCustomUseCase } from '@/core/domain/recurly/usecases/recurly-create-purchase-custom.usecase';
import { UserDetails } from '@/data/auth/services/ezy/responses/interfaces/user-details.interface';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { EMPTY, catchError, of, switchMap } from 'rxjs';
import { setProgressAction } from '../../../../data/current-progress/store/current-progress.actions';
import { PROGRESS_NAME } from '../../../../ui/shared/ui/vg-progress/vg-progress.component';

@Component({
    selector: 'vg-payment',
    templateUrl: './payment.component.html',
    styleUrls: ['./payment.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PaymentComponent implements AfterViewInit {
    public purchasePersistence: PurchasePersistenceModel | null = null;

    public formPayment: {
        valid: boolean;
        data?: any;
    } = { valid: false };

    public showModal3ds = false;

    public loading = false;

    public userInfo: UserDetails | null = null;

    public responseCardToken: CardToken = {};

    public msgError = '';

    public showGenericError = false;

    public showLoader = false;

    public isError = false;

    public threeDSecure: any = null;

    public isUserLogged = false;

    constructor(
        // eslint-disable-next-line @ngrx/no-typed-global-store
        private readonly store: Store<{ currentProgress: PROGRESS_NAME }>,
        private readonly getPurchasePersistence: GetPurchaseUseCase,
        private readonly createCardTokenUseCase: CreateTokenCardUseCase,
        private readonly createPurchaseCustomUseCase: CreatePurchaseCustomUseCase,
        private readonly cdref: ChangeDetectorRef,
        private readonly translate: TranslateService,
        private readonly router: Router,
        private readonly url: UrlService,
        private readonly gtmDataLayer: SetDataLayerParamsUseCase,
        private readonly authCheckLogin: CheckLoginUseCase,
    ) {
        this.store.dispatch(setProgressAction({ value: 'payment' }));
        this.initPurchasePersistence();
        this.initGetInfoUser();
        this.gtmPageView();
    }
    ngAfterViewInit(): void {
        const originalElement = document.querySelector('.grecaptcha-badge');
        const parentElement = document.querySelector('vg-payment');

        if (!parentElement?.contains(originalElement)) {
            const clonedElement = originalElement?.cloneNode(
                true,
            ) as HTMLElement;
            const container = document.querySelector('vg-payment');
            container?.appendChild(clonedElement);
        }
    }

    public onCloseError(): void {
        this.loading = false;
        this.cdref.detectChanges();
    }

    public onCloseError3ds(): void {
        this.loading = false;
        this.showModal3ds = false;
        document.querySelector('body')?.classList.remove('overflow-hidden');
        this.threeDSecure.remove();
    }

    public onChangesFormPayment(changesFormPayment: {
        valid: boolean;
        data?: any;
    }): void {
        this.formPayment = changesFormPayment;
    }

    public open3dsecure(actionTokenIdResponse: string): void {
        const risk = (<any>window).recurly.Risk();
        this.threeDSecure = risk.ThreeDSecure({
            actionTokenId: actionTokenIdResponse,
        });
        this.threeDSecure.on('token', (token: { id: string; type: string }) => {
            this.threeDSecure.remove();
            document.querySelector('body')?.classList.remove('overflow-hidden');
            this.showModal3ds = false;
            this.showLoader = true;
            this.cdref.detectChanges();
            this.onSuccess3dSecure(token.id);
        });

        this.threeDSecure.on('error', (error: unknown) => {
            document.querySelector('body')?.classList.remove('overflow-hidden');
            this.threeDSecure.remove();
            this.showModal3ds = false;
            this.msgError = this.translate.instant(
                'vg_purchase_payment_error-3dsecure',
            );
            this.showGenericError = true;
            this.cdref.detectChanges();
        });
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.threeDSecure.attach(
            document.querySelector('.container-3d-secure')!,
        );
        document.querySelector('body')?.classList.add('overflow-hidden');
        this.showModal3ds = true;
    }

    public goToConfirmartion(purchasedCard: unknown): void {
        this.url.format('purchase/confirmation').subscribe((url) =>
            this.router.navigateByUrl(url, {
                state: { card: purchasedCard },
            }),
        );
    }

    public onSuccess3dSecure(tokenId3ds: string): void {
        this.createPurchaseCustomUseCase
            .execute(
                this.createPurchaseCustomRequest(
                    this.responseCardToken,
                    tokenId3ds,
                ),
            )
            .subscribe({
                next: (response: any) => {
                    this.loading = false;
                    this.showLoader = false;
                    this.goToConfirmartion(response.data);
                },
                error: (error) => {
                    this.showLoader = false;
                    this.onErrorCreatePurchaseCustom(error);
                },
            });
    }

    public onFinish(): void {
        this.loading = true;
        this.showLoader = true;
        this.showGenericError = false;
        this.createCardTokenUseCase
            .execute(this.createCardTokenRequest())
            .pipe(
                catchError((error) => {
                    this.showLoader = false;
                    this.onErrorCreateCardToken(error);
                    return EMPTY;
                }),
                switchMap((responseCardToken: CardToken) => {
                    this.responseCardToken = responseCardToken;
                    return this.createPurchaseCustomUseCase.execute(
                        this.createPurchaseCustomRequest(responseCardToken),
                    );
                }),
            )
            .subscribe({
                next: (response: any) => {
                    this.showLoader = false;
                    this.goToConfirmartion(response.data);
                },
                error: (error) => {
                    this.showLoader = false;
                    this.onErrorCreatePurchaseCustom(error);
                },
            });
    }

    public onErrorCreatePurchaseCustom(responseCreatePurchaseCustom: {
        error: {
            type: string;
            code: string;
            message: string;
            fields: string[];
            parameters: { key: string; message: string; value: string }[];
        };
    }): void {
        if (
            responseCreatePurchaseCustom?.error?.parameters?.some(
                (item) => item.key === 'threeDSecureActionTokenId',
            )
        ) {
            this.open3dsecure(
                responseCreatePurchaseCustom?.error?.parameters?.find(
                    (item) => item.key === 'threeDSecureActionTokenId',
                )?.value as string,
            );
        } else {
            this.msgError = this.translate.instant(
                'vg_purchase_payment_error-3dsecure',
            );
            this.showGenericError = true;
        }
        this.gtmError(
            responseCreatePurchaseCustom.error.type,
            responseCreatePurchaseCustom.error.code,
            responseCreatePurchaseCustom.error.message,
        );
    }

    public onErrorCreateCardToken(responseCardTokenError: {
        error: {
            code: string;
            fields: string[];
            message: string;
        };
    }): void {
        if (responseCardTokenError?.error?.fields?.includes('number')) {
            // TODO: set error invalid card
        }
        this.msgError = this.translate.instant(
            'vg_purchase_payment_error-3dsecure',
        );
        this.showGenericError = true;
        this.gtmError(
            'ERROR',
            responseCardTokenError.error.code.toUpperCase(),
            responseCardTokenError.error.message,
        );
    }

    private createCardTokenRequest(): CreateCardTokenRecurlyUseCaseInput {
        return {
            number: this.formPayment.data?.cardNumber,
            first_name: this.formPayment.data?.firstName,
            last_name: this.formPayment.data?.lastName,
            address1: this.formPayment.data?.address,
            city: this.formPayment.data?.city,
            state: this.formPayment.data?.state,
            postal_code: this.formPayment.data?.postalCode,
            country: 'MX',
            month: DateTime.fromFormat(
                this.formPayment.data?.expirationDate,
                'LLyy',
            ).toFormat('LL'),
            year: DateTime.fromFormat(
                this.formPayment.data?.expirationDate,
                'LLyy',
            ).toFormat('yyyy'),
            cvv: this.formPayment.data?.cvv,
        };
    }

    public createPurchaseCustomRequest(
        responseCardToken: CardToken,
        tokenId3ds: string | null = null,
    ): PurchaseCustomModel {
        const request: PurchaseCustomModel = {
            gifter: {
                name: this.purchasePersistence?.message?.from as string,
                email: this.userInfo?.email as string,
            },
            cardAmount: this.purchasePersistence?.amount as number,
            cardDesign: this.purchasePersistence?.cardDesign?.id as string,
            cardMessage: this.purchasePersistence?.message?.message as string,
            paymentToken: responseCardToken?.id as string,
            cardsQuantity: 1,
            cards: [
                {
                    type: 'dedicated',
                    name: this.purchasePersistence?.shipment
                        ?.recipientName as string,
                    email: this.purchasePersistence?.shipment
                        ?.recipientEmail as string,
                },
            ],
        };
        if (this.purchasePersistence?.shipment?.scheduleShipping === true) {
            request.deliveryDate =
                this.purchasePersistence?.shipment?.recipientScheduleShipping;
        }
        if (tokenId3ds !== null) {
            request.tokenId3ds = tokenId3ds;
        }
        return request;
    }

    public initGetInfoUser(): void {
        this.authCheckLogin
            .execute()
            .pipe(
                switchMap((auth) => {
                    if (auth && auth?.details) {
                        this.isUserLogged = true;
                    }

                    return of(auth?.details);
                }),
            )
            .subscribe((details) => {
                this.userInfo = details!;
            });
    }

    public initPurchasePersistence(): void {
        this.getPurchasePersistence
            .execute()
            .subscribe((purchasePersistence) => {
                this.purchasePersistence = purchasePersistence;
            });
    }

    gtmPageView() {
        this.gtmDataLayer.execute<GtmPageView>({
            event: 'ev_pageview',
            page_path: this.router.url,
            page_title: 'payment',
        });
        this.gtmDataLayer.execute<GtmCheckOut>({
            event: 'checkout',
            category: 'viva_gift',
            action: 'checkout',
            label: 'payment',
            login: this.isUserLogged ? 'Yes' : 'No',
            checkout: { step: '5', step_name: 'payment' },
        });
    }

    gtmError(type: string, code: string, message: string) {
        const translatedCode = this.translate.instant('vg_purchase_payment_error-3dsecure');
        this.gtmDataLayer.execute<GtmError>({
            event: 'ev_message',
            message: {
                type: `${type} | ${translatedCode}`,
                code: code,
                message: message,
            },
        });
    }
}
